import type { ParseError } from "@/parser/types";

interface Frontmatter {
  name: string;
  id?: string;
  variables: string[];
  isSubTemplate: boolean;
  category?: string;
}

function applyYamlLine(frontmatter: Frontmatter, key: string, rawValue: string): void {
  switch (key) {
    case "name": {
      frontmatter.name = rawValue;
      break;
    }
    case "id": {
      frontmatter.id = rawValue || undefined;
      break;
    }
    case "sub-template": {
      frontmatter.isSubTemplate = rawValue === "true";
      break;
    }
    case "category": {
      frontmatter.category = rawValue || undefined;
      break;
    }
    case "variables": {
      frontmatter.variables = rawValue
        .split(",")
        .map((v) => v.trim())
        .filter(Boolean);
      break;
    }
    // No default
  }
}

function parseYamlBlock(yamlBlock: string, frontmatter: Frontmatter): void {
  for (const line of yamlBlock.split("\n")) {
    const colonIdx = line.indexOf(":");
    if (colonIdx === -1) {
      continue;
    }
    applyYamlLine(frontmatter, line.slice(0, colonIdx).trim(), line.slice(colonIdx + 1).trim());
  }
}

export function parseFrontmatter(
  source: string,
  errors: ParseError[],
): { frontmatter: Frontmatter; bodyContent: string } {
  const frontmatter: Frontmatter = { isSubTemplate: false, name: "", variables: [] };

  if (!source.trimStart().startsWith("---")) {
    errors.push({ line: 1, message: "Expected frontmatter starting with ---" });
    return { bodyContent: source, frontmatter };
  }

  const closingIdx = source.indexOf("\n---", 3);
  if (closingIdx === -1) {
    errors.push({ line: 1, message: "Unclosed frontmatter: missing closing ---" });
    return { bodyContent: source, frontmatter };
  }

  const yamlBlock = source.slice(4, closingIdx);
  let bodyContent = source.slice(closingIdx + 4);
  if (bodyContent.startsWith("\n")) {
    bodyContent = bodyContent.slice(1);
  }

  parseYamlBlock(yamlBlock, frontmatter);

  if (!frontmatter.name) {
    errors.push({ line: 1, message: 'Frontmatter missing required "name" field' });
  }
  return { bodyContent, frontmatter };
}
